import { ArrayElement } from '@easeltv/utilities';
import * as zod from 'zod';
import { ZodTypeAny } from 'zod';

import {
    AgeRating,
    Banner,
    Category,
    CategoryType,
    Collection,
    Config,
    Dictionary,
    Footer,
    Label,
    Menu,
    Page,
    Person,
    PersonType,
    Product,
    SubscriptionResponse,
    Theme,
} from '../models';

/* ---------------------------------- Types --------------------------------- */

/**
 * EntityCache
 *
 * The shape of the objects stored in the cache
 */
export interface EntityCache<T> {
    /** The date after which the data needs to be re-fetched */
    expires: string;

    /** Data for that entity */
    data: T;
}

/**
 * EntityFromCachedEntity
 *
 * Reverse engineers EntityCache to get the underlying type
 */
export type EntityFromCachedEntity<Cache extends EntityCache<unknown>> =
    Cache extends EntityCache<infer Type> ? Type : never;

/**
 * cacheEntity
 *
 * Function to wrap an entity with the standard cache properties
 *
 * @param schema - The Zod schema for the entity to wrap in a cache object
 * @returns A ZodType
 */
export const cacheEntity = <T extends ZodTypeAny>(schema: T) =>
    zod.object({
        expires: zod.string(),
        data: schema,
    });

/**
 * stripInvalidArray
 *
 * Wrapper function to remove any unconforming items from an array
 *
 * @param schema - The Zod schema for the item in the array
 * @returns A ZodType
 */
export function stripInvalidArray<T extends ZodTypeAny>(schema: T) {
    return zod.preprocess((inputBars) => {
        const result = zod.array(zod.any()).default([]).safeParse(inputBars);

        if (!result.success) {
            // eslint-disable-next-line no-console
            console.log(result);

            return [];
        }

        return result.data.filter((bar) => schema.safeParse(bar).success);
    }, zod.array(schema).default([]));
}

/**
 * CacheSchema
 *
 * The structure of the cache object
 * Schema object for the cache, used to validate the incoming cache object
 */
export const CacheSchema = zod.object({
    ageRating: stripInvalidArray(cacheEntity(AgeRating)),
    banner: stripInvalidArray(cacheEntity(Banner)),
    category: stripInvalidArray(cacheEntity(Category)),
    categoryType: stripInvalidArray(cacheEntity(CategoryType)),
    collection: stripInvalidArray(cacheEntity(Collection)),
    config: stripInvalidArray(cacheEntity(Config)),
    dictionary: stripInvalidArray(cacheEntity(Dictionary)),
    footer: stripInvalidArray(cacheEntity(Footer)),
    label: stripInvalidArray(cacheEntity(Label)),
    menu: stripInvalidArray(cacheEntity(Menu)),
    page: stripInvalidArray(cacheEntity(Page)),
    person: stripInvalidArray(cacheEntity(Person)),
    personType: stripInvalidArray(cacheEntity(PersonType)),
    product: stripInvalidArray(cacheEntity(Product)),
    subscription: stripInvalidArray(cacheEntity(SubscriptionResponse)),
    theme: stripInvalidArray(cacheEntity(Theme)),
});
export type CacheSchema = zod.TypeOf<typeof CacheSchema>;

/**
 * CacheMap
 *
 * Interface maps cache keys to the interface of the object that is store within
 * Gets the CachedEntity out of the ArrayElement of the CacheSchema with the given key (K)
 */
export type CacheMap = {
    [K in keyof CacheSchema]: EntityFromCachedEntity<
        ArrayElement<CacheSchema[K]>
    >;
};

/**
 * CacheMode
 *
 * CacheMode dictates how and if we store responses from api requests and as a result if we fetch
 * from the cache when making requests
 */
type CacheMode = 'off' | 'session' | 'on';

/* -------------------------------- DataCache ------------------------------- */

/**
 * DataCache
 *
 * Class that stores and manages access to the cache
 */
export class DataCache {
    /** The cache object */
    cache: CacheSchema = {
        ageRating: [],
        banner: [],
        category: [],
        categoryType: [],
        collection: [],
        config: [],
        dictionary: [],
        footer: [],
        label: [],
        menu: [],
        page: [],
        person: [],
        personType: [],
        product: [],
        subscription: [],
        theme: [],
    };

    /** The cache mode */
    cacheMode: CacheMode = 'on';

    /**
     * constructor
     *
     * @param restoredCache - A locally stored copy of the cache
     */
    constructor(restoredCache?: string | undefined) {
        if (restoredCache) this.restore(restoredCache);
    }

    /**
     * getData
     *
     * Gets a single item from the cache of the given key
     *
     * @param key The location in the cache to fetch the data from
     * @param identifierName The unique property being used to search for the item
     * @param identifier The value of the unique property being searched for
     * @returns An item of the Type referenced in the CacheMap
     */
    getData = <
        Key extends keyof CacheMap,
        Type extends CacheMap[Key],
        Name extends keyof Type,
        Identifier extends Type[Name],
    >(
        key: Key,
        identifierName: Name,
        identifier: Identifier,
    ): Type | undefined => {
        if (this.cacheMode === 'off') return undefined;

        // Weird TS bug to do with call signatures of union types means we have to declare this type
        // https://github.com/microsoft/TypeScript/issues/7294
        const cache = this.cache[key] as EntityCache<Type>[];

        // Try and find item in cache
        const entry = cache.find(
            (item) => item.data[identifierName] === identifier,
        );

        // If it doesn't exist return out
        if (!entry) {
            return undefined;
        }

        // If an item exists, parse the expiry into a DateTime object to compare with the current DateTime
        const expiry = new Date(entry.expires);
        const now = new Date();

        if (now > expiry) {
            // If the item has expired then remove it from the cache
            this.removeData<Key, Type, Name, Identifier>(
                key,
                identifierName,
                identifier,
            );
            return undefined;
        }

        // Else return the cached item
        return entry.data;
    };

    /**
     * getDataFirst
     *
     * Gets the first item from the cache of the given key
     * This is useful for resources that don't have ids or we only store one of
     * Menu, Theme, Config, etc
     *
     * @param key The location in the cache to fetch the data from
     * @returns An item of the Type referenced in the CacheMap
     */
    getDataFirst = <Key extends keyof CacheMap, Type extends CacheMap[Key]>(
        key: Key,
    ): Type | undefined => {
        if (this.cacheMode === 'off') return undefined;

        // Weird TS bug to do with call signatures of union types means we have to declare this type
        // https://github.com/microsoft/TypeScript/issues/7294
        const cache = this.cache[key] as EntityCache<Type>[];

        // Get the first item in the cache
        const entry = cache[0];

        // If it doesn't exist return out
        if (!entry) {
            return undefined;
        }

        // If an item exists, parse the expiry into a DateTime object to compare with the current DateTime
        const expiry = new Date(entry.expires);
        const now = new Date();

        if (now > expiry) {
            // If the item has expired then remove it from the cache
            this.removeDataIndex(key, 0);
            return undefined;
        }

        // Else return the cached item
        return entry.data;
    };

    /**
     * getDataSet
     *
     * Gets a group items from the cache of the given key
     *
     * @param key The location in the cache to fetch the data from
     * @param identifierName The unique property being used to search for the item
     * @param identifiers An array of values of the unique property being searched for
     * @returns An array of the Type referenced in the CacheMap
     */
    getDataSet = <
        Key extends keyof CacheMap,
        Type extends CacheMap[Key],
        Name extends keyof Type,
        Identifier extends Type[Name],
    >(
        key: Key,
        identifierName: Name,
        identifiers: Identifier[],
    ): [Type[], Identifier[]] => {
        if (this.cacheMode === 'off') return [[], identifiers];

        const cachedItems: Type[] = [];
        const unCachedItems: Identifier[] = [];

        // Weird TS bug to do with call signatures of union types means we have to declare this type
        // https://github.com/microsoft/TypeScript/issues/7294
        const cache = this.cache[key] as EntityCache<Type>[];

        // For each item try and find it in the cache
        identifiers.forEach((id) => {
            const entry = cache.find(
                (item) => item.data[identifierName] === id,
            );

            // If it doesn't exist, add it to the array of un-cached items
            if (!entry) {
                return unCachedItems.push(id);
            }

            // If an item exists, parse the expiry into a DateTime object to compare with the current DateTime
            const expiry = new Date(entry.expires);
            const now = new Date();

            if (now > expiry) {
                // If the item has expired then add it to the array of un-cached items
                this.removeData<Key, Type, Name, Identifier>(
                    key,
                    identifierName,
                    id,
                );
                return unCachedItems.push(id);
            }

            // Else return add the item to the array of cached items
            return cachedItems.push(entry.data);
        });

        // Return a list of both cached and un-cached items so only the un-cached ones are requested
        return [cachedItems, unCachedItems];
    };

    /**
     * setData
     *
     * Add new data to the cache at the given key
     *
     * @param key The location in the cache to store the data
     * @param data The data to store
     */
    setData<Key extends keyof CacheMap, Type extends CacheMap[Key]>(
        key: Key,
        data: Type,
        expires: string,
    ): void;

    setData<Key extends keyof CacheMap, Type extends CacheMap[Key]>(
        key: Key,
        data: Type[],
        expires: (data: Type) => string,
    ): void;

    setData<Key extends keyof CacheMap, Type extends CacheMap[Key]>(
        key: Key,
        data: Type | Type[],
        expires: string | ((data: Type) => string),
    ): void {
        if (this.cacheMode === 'off') return;

        // If setting an array of items to the cache
        if (Array.isArray(data)) {
            const updatedCache: CacheSchema = {
                ...this.cache,
                [key]: [
                    ...this.cache[key],
                    ...data.map((item) => ({
                        // Calculate the expiry for each one using the given expiry string or function
                        expires:
                            typeof expires === 'function'
                                ? expires(item)
                                : expires,
                        data: item,
                    })),
                ],
            };

            // Update the cache
            this.cache = updatedCache;
        } else {
            // If only one item is being added
            const updatedCache: CacheSchema = {
                ...this.cache,
                [key]: [
                    ...this.cache[key],
                    {
                        data,
                        // Calculate the expiry for the item using the given expiry string or function
                        expires:
                            typeof expires === 'function'
                                ? expires(data)
                                : expires,
                    },
                ],
            };

            // Update the cache
            this.cache = updatedCache;
        }

        // Override the updated cache in local storage
        if (this.cacheMode === 'on') {
            localStorage.setItem('easel.cache', JSON.stringify(this.cache));
        }
    }

    /**
     * removeData
     *
     * Removes an item from the cache at the given key, using an identifier
     *
     * @param key The location in the cache to fetch the data from
     * @param identifierName The unique property being used to search for the item
     * @param identifier The value of the unique property being searched for
     * @returns An array of the Type referenced in the CacheMap
     */
    removeData<
        Key extends keyof CacheMap,
        Type extends CacheMap[Key],
        Name extends keyof Type,
        Identifier extends Type[Name],
    >(key: Key, identifierName: Name, identifier: Identifier): void {
        if (this.cacheMode === 'off') return;

        // Make a copy of the cache
        // Weird TS bug to do with call signatures of union types means we have to declare this type
        // https://github.com/microsoft/TypeScript/issues/7294
        const cache = [...this.cache[key]] as EntityCache<Type>[];

        // Try and find the index of the item to be removed
        const index = cache.findIndex(
            (entity) => entity.data[identifierName] === identifier,
        );

        // If it exists in the cache array then splice it out
        if (index > -1) cache.splice(index, 1);

        const updatedCache: CacheSchema = {
            ...this.cache,
            [key]: cache,
        };

        // Update the cache without the removed item
        this.cache = updatedCache;

        // Override the updated cache in local storage
        if (this.cacheMode === 'on') {
            localStorage.setItem('easel.cache', JSON.stringify(this.cache));
        }
    }

    /**
     * removeDataIndex
     *
     * Removes an item from the cache at the given key, using an array index
     *
     * @param key - The location in the cache to fetch the data from
     * @param index - Array index position of the item
     */
    removeDataIndex<Key extends keyof CacheMap, Type extends CacheMap[Key]>(
        key: Key,
        index: number,
    ): void {
        if (this.cacheMode === 'off') return;

        // Make a copy of the cache
        // Weird TS bug to do with call signatures of union types means we have to declare this type
        // https://github.com/microsoft/TypeScript/issues/7294
        const cache = [...this.cache[key]] as EntityCache<Type>[];

        // Splice out the index of the item to be removed
        cache.splice(index, 1);

        const updatedCache: CacheSchema = {
            ...this.cache,
            [key]: cache,
        };

        // Update the cache without the removed item
        this.cache = updatedCache;

        // Override the updated cache in local storage
        if (this.cacheMode === 'on') {
            localStorage.setItem('easel.cache', JSON.stringify(this.cache));
        }
    }

    /**
     * restore
     *
     * Restore the cache during start up from a locally stored version
     *
     * @param restoredCache - A locally stored copy of the cache
     */
    restore = (restoredCache: unknown) => {
        // Parse the given cache and make sure that the schema is followed
        const validationResult = CacheSchema.safeParse(restoredCache);

        // If validation fails we stick with the empty default cache
        if (!validationResult.success) return;

        if (validationResult.data.config?.[0]?.data.cache.mode !== 'on') {
            localStorage.removeItem('easel.cache');
        } else {
            // On success, we can replace the cache with the data coming out of the validationResult
            // Any new entities added to the cache will be added as empty arrays
            // Any entities that have changed shape and no longer conform will be stripped from the arrays so re-fetched
            // when appropriate
            this.cache = {
                ...validationResult.data,
            };
        }

        this.setCacheMode(validationResult.data.config?.[0]?.data.cache.mode);
    };

    /**
     * setCacheMode
     *
     * Update the cache mode to change the cache behaviour
     *
     * @param mode - Type of cache behaviour to follow
     */
    setCacheMode = (mode: CacheMode): void => {
        this.cacheMode = mode;
    };
}
